import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  GlobalWorkerOptions,
  PDFDocumentProxy,
  PDFDocumentLoadingTask,
  RenderTask,
  getDocument,
  version as pdfjsVersion,
} from 'pdfjs-dist';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Message } from 'primeng/message';
import { ButtonModule } from 'primeng/button';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { pdfFitWidthPx, pdfRenderScale } from './pdf-render-scale';

const MAX_PDF_PAGES = 5;
// Copied from pdfjs-dist into assets at build time. The filename is not
// content-hashed and /assets/ is served immutable for a year, so the version
// query keeps the worker in step with the pdfjs API after an upgrade.
const PDF_WORKER_SRC = `/assets/pdf.worker.min.mjs?v=${pdfjsVersion}`;
GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;

@Component({
  selector: 'app-pdf-viewer',
  imports: [ProgressSpinner, Message, ButtonModule, Button, Tooltip],
  templateUrl: './pdf-viewer.component.html',
  styleUrl: './pdf-viewer.component.scss',
})
export class PdfViewerComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() fileUrl: string | null = null;
  @Input() emptyMessage = 'No PDF is available for this drawing.';
  @Input() initialZoom = 1;

  /** Pane used to fit pages. It does not wrap the rendered canvases. */
  @ViewChild('fitWidth')
  fitWidth?: ElementRef<HTMLDivElement>;

  @ViewChild('pagesContainer')
  pagesContainer?: ElementRef<HTMLDivElement>;

  loading = false;
  errorMessage: string | null = null;
  totalPages = 0;
  zoomFactor = 1;

  /** 1 is the page fitted to the viewer. Smaller values shrink the drawing. */
  private readonly minZoom = 1;
  private readonly maxZoom = 3;
  private readonly zoomStep = 0.25;

  private readonly http = inject(HttpClient);
  private loadSubscription: Subscription | null = null;
  private pdfDocument: PDFDocumentProxy | null = null;
  private pdfLoadingTask: PDFDocumentLoadingTask | null = null;
  private renderTasks: RenderTask[] = [];
  private renderGeneration = 0;
  private viewReady = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fileUrl']) {
      this.loadPdf();
    }

    if (changes['initialZoom'] && this.initialZoom > 0) {
      this.zoomFactor = Math.max(
        this.minZoom,
        Math.min(this.maxZoom, this.initialZoom),
      );
      // If a PDF is already loaded, re-render at the new zoom.
      if (this.pdfDocument) {
        this.rerenderForZoom();
      }
    }
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    // After the first check, so loading state does not change mid-cycle.
    queueMicrotask(() => {
      if (this.viewReady) {
        this.loadPdf();
      }
    });
  }

  ngOnDestroy(): void {
    this.viewReady = false;
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = null;
    void this.cleanupPdf();
  }

  private loadPdf(): void {
    this.renderGeneration += 1;
    const generation = this.renderGeneration;

    this.resetStateForNewLoad();

    const trimmedUrl = this.fileUrl?.trim() ?? '';
    if (!trimmedUrl) {
      this.errorMessage = this.emptyMessage;
      this.loading = false;
      return;
    }

    if (!this.viewReady || !this.pagesContainer) {
      return;
    }

    this.loading = true;
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.http
      .get(trimmedUrl, { responseType: 'arraybuffer' })
      .subscribe({
        next: async (pdfBuffer) => {
          await this.renderPdf(pdfBuffer, generation);
        },
        error: () => {
          if (generation !== this.renderGeneration) {
            return;
          }
          this.loading = false;
          this.errorMessage = 'Unable to load this PDF preview.';
        },
      });
  }

  private async renderPdf(
    pdfBuffer: ArrayBuffer,
    generation: number,
  ): Promise<void> {
    try {
      await this.cleanupPdf();
      if (generation !== this.renderGeneration) {
        return;
      }

      const loadingTask = getDocument({ data: pdfBuffer });
      this.pdfLoadingTask = loadingTask;
      const pdf = await loadingTask.promise;

      if (generation !== this.renderGeneration) {
        await loadingTask.destroy();
        return;
      }

      this.pdfDocument = pdf;
      this.totalPages = pdf.numPages;

      await this.renderPages(pdf, generation);
    } catch {
      if (generation !== this.renderGeneration) {
        return;
      }
      this.loading = false;
      this.errorMessage = 'Unable to render this PDF preview.';
    }
  }

  private async renderPages(
    pdf: PDFDocumentProxy,
    generation: number,
  ): Promise<void> {
    const container = this.pagesContainer?.nativeElement;
    if (!container) {
      this.loading = false;
      this.errorMessage = 'PDF preview container is unavailable.';
      return;
    }

    this.clearRenderedCanvases();
    this.cancelRenderTasks();

    const pagesToRender = Math.min(pdf.numPages, MAX_PDF_PAGES);

    // Keep frontend rendering aligned with the backend PDF page limit.
    for (let pageNumber = 1; pageNumber <= pagesToRender; pageNumber += 1) {
      if (generation !== this.renderGeneration) {
        break;
      }

      const page = await pdf.getPage(pageNumber);
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = pdfRenderScale(
        this.fitWidthPx(),
        baseViewport.width,
        this.zoomFactor,
      );
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.className = 'pdf-page-canvas';
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);

      const context = canvas.getContext('2d', { alpha: false });
      if (!context) {
        page.cleanup();
        throw new Error('Canvas context unavailable');
      }

      container.appendChild(canvas);
      const renderTask = page.render({
        canvasContext: context,
        viewport,
        canvas,
      });
      this.renderTasks.push(renderTask);
      try {
        await renderTask.promise;
      } catch {
        // Most commonly triggered by cancellation on a newer render generation.
        if (generation !== this.renderGeneration) {
          break;
        }
        throw new Error('PDF page render failed');
      }
      page.cleanup();
    }

    if (generation === this.renderGeneration) {
      this.loading = false;
    }
  }

  private resetStateForNewLoad(): void {
    this.loading = false;
    this.errorMessage = null;
    this.totalPages = 0;
    this.clearRenderedCanvases();
    this.cancelRenderTasks();
  }

  private async cleanupPdf(): Promise<void> {
    this.cancelRenderTasks();
    this.clearRenderedCanvases();
    if (this.pdfDocument) {
      this.pdfDocument.cleanup();
      this.pdfDocument = null;
    }
    if (this.pdfLoadingTask) {
      await this.pdfLoadingTask.destroy();
      this.pdfLoadingTask = null;
    }
  }

  private cancelRenderTasks(): void {
    for (const task of this.renderTasks) {
      task.cancel();
    }
    this.renderTasks = [];
  }

  private clearRenderedCanvases(): void {
    const container = this.pagesContainer?.nativeElement;
    if (!container) {
      return;
    }
    container.innerHTML = '';
  }

  /** Width the next zoom fits to. Independent of canvases already drawn. */
  fitWidthPx(): number {
    const element =
      this.fitWidth?.nativeElement ?? this.pagesContainer?.nativeElement;
    if (!element) {
      return 320;
    }
    return pdfFitWidthPx(element);
  }

  get canZoomOut(): boolean {
    return this.zoomFactor > this.minZoom + 0.0001;
  }

  get canZoomIn(): boolean {
    return this.zoomFactor < this.maxZoom - 0.0001;
  }

  zoomIn(): void {
    this.setZoom(this.zoomFactor + this.zoomStep);
  }

  zoomOut(): void {
    this.setZoom(this.zoomFactor - this.zoomStep);
  }

  resetZoom(): void {
    this.setZoom(this.minZoom);
  }

  private setZoom(nextZoom: number): void {
    const clamped = Math.max(this.minZoom, Math.min(this.maxZoom, nextZoom));
    if (Math.abs(clamped - this.zoomFactor) < 0.0001) {
      return;
    }
    this.zoomFactor = clamped;
    if (this.pdfDocument) {
      this.rerenderForZoom();
    }
  }

  private rerenderForZoom(): void {
    if (!this.pdfDocument) {
      return;
    }
    if (!this.viewReady) {
      return;
    }

    this.renderGeneration += 1;
    const generation = this.renderGeneration;
    this.loading = true;
    this.errorMessage = null;

    void this.renderPages(this.pdfDocument, generation).catch(() => {
      if (generation !== this.renderGeneration) {
        return;
      }
      this.loading = false;
      this.errorMessage = 'Unable to render this PDF preview.';
    });
  }

  get zoomPercent(): string {
    return `${Math.round(this.zoomFactor * 100)}%`;
  }
}
